import Intercom, {
  hide as sdkHide,
  onHide as sdkOnHide,
  onUnreadCountChange as sdkOnUnreadCountChange,
  showMessages as sdkShowMessages,
  showNewMessage as sdkShowNewMessage,
  shutdown as sdkShutdown,
  update as sdkUpdate,
} from '@intercom/messenger-js-sdk';

export interface IntercomSettings {
  app_id: string;
  user_id?: string;
  name?: string;
  email?: string;
  created_at?: number;
  [key: string]: any;
}

let isInitialized = false;
let customOnHideCallback: (() => void) | null = null;
let pendingUserSettings: IntercomSettings | null = null;

let unreadCountListenerInstalled = false;
let currentUnreadCount = 0;
const unreadCountCallbacks: Set<(count: number) => void> = new Set();

const getGlobal = () => (typeof globalThis === 'undefined' ? undefined : (globalThis as any));

const isBrowser = () => {
  const g = getGlobal();
  return !!g && !!g.window;
};

export const loadIntercomScript = (appId: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (!isBrowser()) {
      resolve();
      return;
    }

    const isValidAppId = /^[a-z0-9]{8}$/.test(appId ?? '');
    if (!isValidAppId) {
      console.error('Invalid Intercom App ID:', appId);
      reject(new Error('Invalid Intercom App ID'));
      return;
    }

    resolve();
  });
};

export const initializeIntercom = (settings: IntercomSettings): Promise<void> => {
  pendingUserSettings = settings;

  return new Promise((resolve) => {
    if (!isBrowser()) {
      resolve();
      return;
    }

    try {
      Intercom({
        app_id: settings.app_id,
        user_id: settings.user_id,
        name: settings.name,
        email: settings.email,
        created_at: settings.created_at,
        hide_default_launcher: true,
        ...settings,
      });

      isInitialized = true;
      try {
        sdkOnHide(() => {
          if (customOnHideCallback) {
            try {
              customOnHideCallback();
            } catch (err) {
              console.error('Error executing onHide callback:', err);
            }
            customOnHideCallback = null;
          }
        });
      } catch (err) {
        console.warn('Failed to install Intercom onHide listener:', err);
      }
    } catch (err) {
      console.error('Error initializing Intercom SDK:', err);
    } finally {
      resolve();
    }
  });
};

export const showIntercom = async () => {
  if (!isBrowser()) {
    return;
  }

  if (!isInitialized) {
    if (!pendingUserSettings) {
      console.error('Cannot initialize Intercom: no user settings');
      return;
    }

    try {
      await initializeIntercom(pendingUserSettings);
    } catch (err) {
      console.error('Failed to initialize Intercom:', err);
      return;
    }
  }

  try {
    sdkShowMessages();
  } catch (err) {
    console.error('Error opening Intercom messenger:', err);
  }
};

export const hideIntercom = () => {
  if (!isBrowser() || !isInitialized) {
    return;
  }

  try {
    sdkHide();
  } catch (err) {
    console.error('Error closing Intercom messenger:', err);
  }
};

export const updateIntercom = (data: Partial<IntercomSettings>) => {
  if (!isBrowser() || !isInitialized) {
    return;
  }

  try {
    sdkUpdate(data);
  } catch (err) {
    console.error('Error updating Intercom:', err);
  }
};

export const shutdownIntercom = () => {
  if (!isBrowser() || !isInitialized) {
    return;
  }

  try {
    sdkShutdown();
  } catch (err) {
    console.error('Error shutting down Intercom:', err);
  } finally {
    isInitialized = false;
    pendingUserSettings = null;
    unreadCountListenerInstalled = false;
    currentUnreadCount = 0;
    unreadCountCallbacks.clear();
  }
};

const installUnreadCountListener = () => {
  if (unreadCountListenerInstalled) {
    return;
  }

  if (!isBrowser()) {
    return;
  }

  try {
    sdkOnUnreadCountChange((count: number) => {
      currentUnreadCount = count;
      unreadCountCallbacks.forEach((callback) => {
        try {
          callback(count);
        } catch (err) {
          console.error('Error in unread count callback:', err);
        }
      });
    });

    unreadCountListenerInstalled = true;
  } catch (err) {
    console.error('Error installing unread count listener:', err);
  }
};

export const getUnreadCount = (): Promise<number> => {
  return new Promise((resolve) => {
    if (!isBrowser()) {
      resolve(0);
      return;
    }

    resolve(currentUnreadCount);
  });
};

export const onUnreadCountChange = (callback: (count: number) => void): (() => void) => {
  installUnreadCountListener();

  unreadCountCallbacks.add(callback);
  callback(currentUnreadCount);

  return () => {
    unreadCountCallbacks.delete(callback);
  };
};

export const onNewMessage = (callback: (message: string) => void): (() => void) => {
  let previousCount = currentUnreadCount;

  const handleCountChange = (count: number) => {
    if (count > previousCount && count > 0) {
      const message = 'Vous avez reçu un nouveau message de notre équipe.';
      callback(message);
    }
    previousCount = count;
  };

  return onUnreadCountChange(handleCountChange);
};

export const setOnHideCallback = (callback: () => void) => {
  customOnHideCallback = callback;
};

export const clearOnHideCallback = () => {
  customOnHideCallback = null;
};

export const showIntercomWithMessage = (message: string) => {
  if (!isBrowser() || !isInitialized) {
    console.error('Intercom not initialized');
    return;
  }

  try {
    sdkShowNewMessage(message);
  } catch (err) {
    console.error('Error opening Intercom messenger with message:', err);
  }
};
